import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    SchemaField,
    createSchemaField,
    deleteSchemaField,
    updateSchemaField,
    invalidateSchemaFieldsCache,
    listSchemaFields,
} from '../../api/schemaField';
import { ApiError } from '../../api/request';
import { listDictCategories, DictCategory } from '../../api/dict';
import { useCan } from '../../store';
import BatchActions, {
    HeaderCheckbox,
    RowCheckbox,
} from '../../components/BatchActions';
import { useBatchSelect } from '../../utils/useBatchSelect';
import { confirmDialog, alertDialog } from '../../utils/dialog';
import SearchSelect from '../../components/SearchSelect';
import { zhOptions } from '../../utils/searchOptions';

// Schema字段管理 (2026-07-31 需求): 参数管理动态 Schema 的字段白名单.
// SSOT = xtool-schema 仓库 (schemas/xtool-studio/project/v1/processing-types.schema.json),
// 镜像行 (source=xtool-schema) 只读 — 改字典必须到 xtool-schema 提 PR 后重跑 seed;
// 平台仅允许新增 custom 字段 (xtool-schema 未覆盖的在用字段), custom 可删.
// 例外 (2026-08-03): 字段名称 (fieldName, 显示名) 两种来源都可编辑, 参数中心展示用.

const TYPE_SELECT_OPTS = ['number', 'string', 'boolean', 'array', 'object'].map(
    (t) => ({ value: t, label: t }),
);
const SOURCE_FILTER_OPTS = [
    { value: 'xtool-schema', label: 'xtool-schema (只读)' },
    { value: 'custom', label: 'custom (平台扩展)' },
];

function SchemaFieldAdmin() {
    const canDelete = useCan('schema_field', 'delete');
    const canEditField = useCan('schema_field', 'update');
    const [list, setList] = useState<SchemaField[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [keyword, setKeyword] = useState('');
    const [grpFilter, setGrpFilter] = useState('');
    const [sourceFilter, setSourceFilter] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    // 编辑态: 非 null 表示在编辑该字段 (key 只读, 存量数据按 key 关联)
    const [editingKey, setEditingKey] = useState<string | null>(null);
    // 编辑 xtool-schema 行时只放开字段名称, 其余属性只读 (后端同样拒绝)
    const [nameOnly, setNameOnly] = useState(false);
    const emptyForm = {
        fieldKey: '',
        fieldName: '',
        fieldType: 'number',
        unit: '',
        grp: '',
        description: '',
        enumsSource: '',
    };
    const [form, setForm] = useState(emptyForm);
    // 枚举候选值 (chip 列表 + 末尾输入框; 类型=number 时保存前转数字)
    const [enums, setEnums] = useState<string[]>([]);
    const [enumDraft, setEnumDraft] = useState('');
    // 字典类目 (供"枚举来源"下拉); 加工模式 mm_group 是独立表, 手动补一项
    const [enumCats, setEnumCats] = useState<DictCategory[]>([]);
    // 枚举来源候选: mm_group (加工模式字典) + 全部字典类目; 只显示中文名, 重名才补 code
    const enumSourceOpts = useMemo(
        () => [
            { value: 'mm_group', label: '加工模式' },
            ...zhOptions(
                enumCats,
                (c) => c.code,
                (c) => c.label,
            ),
        ],
        [enumCats],
    );
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setList(await listSchemaFields());
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
            setList([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        listDictCategories(true)
            .then(setEnumCats)
            .catch(() => setEnumCats([]));
    }, []);

    const grps = useMemo(
        () => Array.from(new Set(list.map((f) => f.grp).filter(Boolean))),
        [list],
    );
    const shown = useMemo(() => {
        const kw = keyword.trim().toLowerCase();
        return list.filter(
            (f) =>
                (!grpFilter || f.grp === grpFilter) &&
                (!sourceFilter || f.source === sourceFilter) &&
                (!kw ||
                    f.fieldKey.toLowerCase().includes(kw) ||
                    (f.fieldName || '').toLowerCase().includes(kw) ||
                    (f.description || '').toLowerCase().includes(kw)),
        );
    }, [list, keyword, grpFilter, sourceFilter]);

    // 批量操作 (2026-09-10): 字段字典不分页, 选中随筛选变化清空
    const sel = useBatchSelect(
        useMemo(() => shown.map((f) => f.fieldKey), [shown]),
        [keyword, grpFilter, sourceFilter].join('|'),
    );

    const counts = useMemo(
        () => ({
            xtool: list.filter((f) => f.source === 'xtool-schema').length,
            custom: list.filter((f) => f.source === 'custom').length,
        }),
        [list],
    );

    const closeForm = () => {
        setShowAdd(false);
        setEditingKey(null);
        setNameOnly(false);
        setForm(emptyForm);
        setEnums([]);
        setEnumDraft('');
    };

    const commitEnumDraft = () => {
        const v = enumDraft.trim();
        if (!v || enums.includes(v)) {
            setEnumDraft('');
            return;
        }
        setEnums([...enums, v]);
        setEnumDraft('');
    };

    const onEdit = (f: SchemaField) => {
        setEditingKey(f.fieldKey);
        setNameOnly(f.source !== 'custom');
        setForm({
            fieldKey: f.fieldKey,
            fieldName: f.fieldName || '',
            fieldType: f.fieldType || 'number',
            unit: f.unit || '',
            grp: f.grp || '',
            description: f.description || '',
            enumsSource: f.enumsSource || '',
        });
        try {
            const parsed =
                f.enums && f.enums !== 'null' ? JSON.parse(f.enums) : [];
            setEnums(Array.isArray(parsed) ? parsed.map(String) : []);
        } catch {
            setEnums([]);
        }
        setShowAdd(true);
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        try {
            // 枚举来源二选一: 配了字典来源 (enumsSource) 就不用内联枚举, 内联清空避免二义
            const src = form.enumsSource.trim();
            // 类型=number 且候选值全是数字时按数字存 (对齐 xtool-schema angleType 的 number+enum 形态)
            const enumVals: (string | number)[] = src
                ? []
                : form.fieldType === 'number' &&
                    enums.every((v) => v !== '' && !Number.isNaN(Number(v)))
                  ? enums.map(Number)
                  : enums;
            // xtool-schema 行仅名称可改, 只发 fieldName (后端收到其它属性会整体拒绝)
            const payload = nameOnly
                ? { fieldName: form.fieldName.trim() }
                : {
                      fieldName: form.fieldName.trim(),
                      fieldType: form.fieldType,
                      unit: form.unit.trim(),
                      grp: form.grp.trim() || '平台自有语义',
                      enums: enumVals, // 编辑时传 [] 即清空
                      enumsSource: src, // 传 '' 清空绑定
                      description: form.description.trim(),
                  };
            const res = editingKey
                ? await updateSchemaField(editingKey, payload)
                : await createSchemaField({
                      fieldKey: form.fieldKey.trim(),
                      ...payload,
                  });
            alertDialog(res.message || (editingKey ? '已更新' : '已新增'));
            invalidateSchemaFieldsCache();
            closeForm();
            await load();
        } catch (err) {
            alertDialog(
                `保存失败: ${err instanceof ApiError ? `${err.code}: ${err.message}` : String(err)}`,
            );
        } finally {
            setBusy(false);
        }
    };

    const onDelete = async (f: SchemaField) => {
        if (
            !(await confirmDialog(
                `确认删除 custom 字段 ${f.fieldKey}? (不影响已存参数数据)`,
            ))
        )
            return;
        try {
            await deleteSchemaField(f.fieldKey);
            invalidateSchemaFieldsCache();
            await load();
        } catch (err) {
            alertDialog(
                `删除失败: ${err instanceof ApiError ? `${err.code}: ${err.message}` : String(err)}`,
            );
        }
    };

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-base font-semibold">
                        Schema 字段管理
                    </div>
                    <div className="text-xs text-slate-500">
                        参数管理动态 Schema 的字段白名单 · xtool-schema 字典{' '}
                        {counts.xtool} 个 (仅名称可改) + 平台扩展{' '}
                        {counts.custom} 个 ·{' '}
                        <span className="text-slate-400">
                            字典行除名称外的修改需到 xtool-schema 仓库提 PR
                            后重跑 seed 同步
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <BatchActions
                        resource="schema_field"
                        entityLabel="Schema 字段"
                        deleteKeyField="fieldKey"
                        selected={sel.selectedKeys}
                        onClear={sel.clear}
                        onDone={load}
                    />
                    {canEditField && (
                        <button
                            type="button"
                            className="text-sm px-3 py-1.5 rounded border border-brand-200 text-brand-600 hover:bg-brand-50"
                            onClick={() => setShowAdd(true)}
                        >
                            + 新增 custom 字段
                        </button>
                    )}
                </div>
            </div>

            <div className="card p-3">
                <div className="flex flex-wrap gap-3 items-end">
                    <div>
                        <label className="field-label">关键词</label>
                        <input
                            type="search"
                            className="inp w-56"
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            placeholder="搜 字段 key / 名称 / 说明"
                        />
                    </div>
                    <div>
                        <label className="field-label">分组</label>
                        <SearchSelect
                            className="w-48"
                            value={grpFilter}
                            onChange={(v) => setGrpFilter(v)}
                            placeholder="全部分组"
                            options={grps.map((g) => ({ value: g, label: g }))}
                            clearable
                        />
                    </div>
                    <div>
                        <label className="field-label">来源</label>
                        <SearchSelect
                            className="w-40"
                            value={sourceFilter}
                            onChange={(v) => setSourceFilter(v)}
                            placeholder="全部"
                            options={SOURCE_FILTER_OPTS}
                            clearable
                        />
                    </div>
                    <span className="ml-auto text-xs text-slate-400">
                        显示 {shown.length} / {list.length} 个
                    </span>
                </div>
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            <div className="card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr>
                            <th className="th w-8" hidden={!canDelete}>
                                <HeaderCheckbox hidden={!canDelete} sel={sel} />
                            </th>
                            <th className="th">字段 key</th>
                            <th className="th w-28">字段名称</th>
                            <th className="th w-24">类型</th>
                            <th className="th w-20">单位</th>
                            <th className="th">分组</th>
                            <th className="th">说明</th>
                            <th className="th w-32">来源</th>
                            <th className="th w-24">操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td
                                    colSpan={9}
                                    className="td text-center text-slate-400 py-8"
                                >
                                    加载中…
                                </td>
                            </tr>
                        ) : shown.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={9}
                                    className="td text-center text-slate-400 py-8"
                                >
                                    无匹配字段
                                </td>
                            </tr>
                        ) : (
                            shown.map((f) => (
                                <tr
                                    key={f.fieldKey}
                                    className="hover:bg-slate-50"
                                >
                                    <td className="td" hidden={!canDelete}>
                                        <RowCheckbox
                                            hidden={!canDelete}
                                            sel={sel}
                                            k={f.fieldKey}
                                        />
                                    </td>
                                    <td className="td font-mono text-xs font-semibold">
                                        {f.fieldKey}
                                    </td>
                                    <td className="td text-xs">
                                        {f.fieldName || (
                                            <span className="text-slate-300">
                                                未命名
                                            </span>
                                        )}
                                    </td>
                                    <td className="td text-xs font-mono">
                                        {f.fieldType || '—'}
                                        {f.enums && f.enums !== 'null' && (
                                            <span
                                                className="text-slate-400"
                                                title={`枚举: ${f.enums}`}
                                            >
                                                {' '}
                                                ⓔ
                                            </span>
                                        )}
                                    </td>
                                    <td className="td text-xs">
                                        {f.unit || '—'}
                                    </td>
                                    <td className="td text-xs">
                                        {f.grp || '—'}
                                    </td>
                                    <td className="td text-xs text-slate-500 max-w-md">
                                        <div
                                            className="line-clamp-2"
                                            title={f.description}
                                        >
                                            {f.description || '—'}
                                        </div>
                                    </td>
                                    <td className="td">
                                        {f.source === 'xtool-schema' ? (
                                            <span
                                                className="tag bg-brand-50 text-brand-700 text-[10px] whitespace-nowrap"
                                                title={f.schemaId}
                                            >
                                                🔒 xtool-schema
                                            </span>
                                        ) : (
                                            <span className="tag bg-amber-50 text-amber-700 text-[10px]">
                                                custom
                                            </span>
                                        )}
                                    </td>
                                    <td className="td text-xs">
                                        {!canEditField ? (
                                            <span className="text-slate-300">
                                                只读
                                            </span>
                                        ) : f.source === 'custom' ? (
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    className="text-brand-600 hover:underline"
                                                    onClick={() => onEdit(f)}
                                                >
                                                    编辑
                                                </button>
                                                <button
                                                    type="button"
                                                    className="text-rose-600 hover:underline"
                                                    onClick={() => onDelete(f)}
                                                >
                                                    删除
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                className="text-brand-600 hover:underline"
                                                title="xtool-schema 字典字段仅名称可改, 其余属性只读"
                                                onClick={() => onEdit(f)}
                                            >
                                                改名
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {showAdd && (
                <div
                    className="fixed inset-0 !m-0 bg-black/30 grid place-items-center z-50"
                    onClick={closeForm}
                >
                    <form
                        className="card bg-white p-5 w-[480px] space-y-3"
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={onSubmit}
                    >
                        <div className="text-sm font-semibold">
                            {editingKey
                                ? `${nameOnly ? '编辑字段名称' : '编辑 custom 字段'} ${editingKey}`
                                : '新增 custom 字段'}
                        </div>
                        <div className="text-xs text-slate-500">
                            {nameOnly
                                ? 'xtool-schema 字典字段仅名称可改 (参数中心展示用); 其余属性请到 xtool-schema 仓库提 PR 后重跑 seed。'
                                : '仅用于 xtool-schema 未覆盖的字段; 与 xtool-schema 同义的字段请直接用字典 key, 长期字段建议到 xtool-schema 提 PR 转正。'}
                        </div>
                        <div>
                            <label className="field-label">
                                字段 key (camelCase) *
                            </label>
                            <input
                                className="inp w-full font-mono disabled:bg-slate-50 disabled:text-slate-400"
                                value={form.fieldKey}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        fieldKey: e.target.value,
                                    })
                                }
                                placeholder="如 nozzleCleanCycle"
                                disabled={!!editingKey}
                                title={
                                    editingKey
                                        ? 'key 不可改 (存量参数数据按 key 关联); 需改名请删旧建新'
                                        : undefined
                                }
                                required
                            />
                        </div>
                        <div>
                            <label className="field-label">
                                字段名称 (显示名)
                            </label>
                            <input
                                className="inp w-full"
                                value={form.fieldName}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        fieldName: e.target.value,
                                    })
                                }
                                placeholder="如 激光功率; 参数中心展示优先用它, 空则回退 key"
                                maxLength={64}
                            />
                        </div>
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <label className="field-label">类型</label>
                                <SearchSelect
                                    className="w-full"
                                    value={form.fieldType}
                                    onChange={(v) =>
                                        setForm({ ...form, fieldType: v })
                                    }
                                    disabled={nameOnly}
                                    options={TYPE_SELECT_OPTS}
                                />
                            </div>
                            <div className="flex-1">
                                <label className="field-label">单位</label>
                                <input
                                    className="inp w-full disabled:bg-slate-50 disabled:text-slate-400"
                                    value={form.unit}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            unit: e.target.value,
                                        })
                                    }
                                    placeholder="mm / % / Hz…"
                                    disabled={nameOnly}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="field-label">分组</label>
                            <SearchSelect
                                className="w-full"
                                value={form.grp}
                                onChange={(v) => setForm({ ...form, grp: v })}
                                options={grps.map((g) => ({ value: g, label: g }))}
                                placeholder="默认: 平台自有语义 (可搜索, 或输入新分组名)"
                                disabled={nameOnly}
                                clearable
                                creatable
                            />
                        </div>
                        <div>
                            <label className="field-label">
                                枚举候选值 (可选)
                            </label>
                            <div className="flex flex-wrap items-center gap-1.5 border border-slate-200 rounded px-2 py-1.5">
                                {enums.map((v) => (
                                    <span
                                        key={v}
                                        className="tag bg-brand-50 text-brand-700"
                                    >
                                        {v}
                                        {!nameOnly && (
                                            <button
                                                type="button"
                                                className="ml-1 hover:text-brand-900"
                                                onClick={() =>
                                                    setEnums(
                                                        enums.filter(
                                                            (x) => x !== v,
                                                        ),
                                                    )
                                                }
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </span>
                                ))}
                                <input
                                    className="flex-1 min-w-24 text-sm outline-none disabled:bg-transparent"
                                    value={enumDraft}
                                    placeholder={
                                        form.enumsSource
                                            ? '已配字典来源, 内联枚举不可用'
                                            : enums.length
                                              ? ''
                                              : '输入候选值, 回车添加 (如 OK / NG)'
                                    }
                                    disabled={nameOnly || !!form.enumsSource}
                                    onChange={(e) =>
                                        setEnumDraft(e.target.value)
                                    }
                                    onBlur={commitEnumDraft}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            commitEnumDraft();
                                        }
                                    }}
                                />
                            </div>
                            <div className="text-[11px] text-slate-400 mt-1">
                                枚举是值约束, 与类型正交 (string/number 均可配);
                                配了枚举后, 参数编辑器里该字段自动渲染为下拉
                            </div>
                        </div>
                        <div>
                            <label className="field-label">
                                枚举来源字典 (可选, 与内联枚举二选一)
                            </label>
                            <SearchSelect
                                className="w-full"
                                value={form.enumsSource}
                                onChange={(v) =>
                                    setForm({ ...form, enumsSource: v })
                                }
                                disabled={nameOnly}
                                placeholder="（无 · 用上方内联枚举 / 不是枚举）"
                                options={enumSourceOpts}
                                clearable
                            />
                            <div className="text-[11px] text-slate-400 mt-1">
                                选定后该字段是"字典来源枚举":
                                取值来自所选字典类目 (字典维护里的光源/波段…),
                                录入渲染字典下拉。 模板字段组只继承此绑定,
                                不能自行发明枚举。
                            </div>
                        </div>
                        <div>
                            <label className="field-label">说明</label>
                            <textarea
                                className="inp w-full h-20 disabled:bg-slate-50 disabled:text-slate-400"
                                value={form.description}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        description: e.target.value,
                                    })
                                }
                                placeholder="字段含义 / 适用设备 / 取值范围"
                                disabled={nameOnly}
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            <button
                                type="button"
                                className="btn-ghost"
                                onClick={closeForm}
                            >
                                取消
                            </button>
                            <button
                                type="submit"
                                className="btn-primary"
                                disabled={busy || !form.fieldKey.trim()}
                            >
                                {busy
                                    ? '提交中…'
                                    : editingKey
                                      ? '保存'
                                      : '新增'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

export default SchemaFieldAdmin;
